import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonProgressBar,
  IonButtons,
  IonButton,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { refresh, returnUpBack } from 'ionicons/icons';
import { APP_SETTINGS_TOKEN, DEFAULT_APP_SETTINGS } from '../../services/app-settings.types';
import { ThemeService } from '../../themes/theme.service';
import { getTheme } from '../../themes/theme.config';
import { Observable, Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { LoadingService, LoadingPhase } from '../../services/loading.service';
import { UserProfileButtonComponent } from '../user-profile-button/user-profile-button.component';
import { CommonModule } from '@angular/common';
import { InfoPinService } from '../../services/info-pin.service';
import { CrowdctrlBackButtonComponent } from '../back-button/back-button.component';

// Register icons used in the header
addIcons({ refresh, returnUpBack });

/**
 * Page component specifically designed for use within Ionic tabs.
 * Provides a header with a back button that uses explicit router navigation
 * to work correctly within tab navigation contexts.
 */
@Component({
  selector: 'crowdctrl-tab-page',
  host: { class: 'ion-page' },
  imports: [
    CommonModule,
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    CrowdctrlBackButtonComponent,
    IonProgressBar,
    IonButtons,
    IonButton,
    IonIcon,
    IonRefresher,
    IonRefresherContent,
    UserProfileButtonComponent,
  ],
  templateUrl: './tab-page.component.html',
  styleUrl: './tab-page.component.css',
  standalone: true,
})
export class TabPageComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  /**
   * Title to display in the header toolbar
   */
  @Input() pageTitle: string = '';

  /**
   * Default href for the back button. If provided, a back button will be shown.
   */
  @Input() defaultHref: string | null = null;

  /**
   * Whether to show the refresh button in the header toolbar.
   * When enabled, clicking the refresh button will emit the refresh event.
   */
  @Input() showRefresh: boolean = false;

  /**
   * When false, ion-content does not scroll; the projected page can use an inner scroll region
   * (e.g. a list). Pull-to-refresh is disabled in that mode (header refresh still runs refreshFn).
   */
  @Input() contentScrollY: boolean = true;

  /**
   * Ionic utility classes on `ion-content` (e.g. `ion-padding`, `ion-padding-bottom`).
   * Default `ion-padding` matches `crowdctrl-page` form screens so `crowdctrl-form-layout` is not flush to edges.
   * Use `ion-padding-bottom` (or `''` plus custom CSS) for full-bleed list / worklist surfaces.
   */
  @Input() contentClass: string = 'ion-padding';

  /**
   * When true, each time this page becomes visible again after being hidden, auto-load runs again
   * (same as first entry). Use for routes that must always refetch when re-entered (e.g. a
   * one-shot status screen); default avoids redundant calls on tab stacks that keep pages alive.
   */
  @Input() reloadDataOnEachViewActivation: boolean = false;
  /**
   * Optional back button icon override.
   */
  @Input() backButtonIcon?: string;

  /**
   * Optional toolbar color override.
   * If provided, this will override the theme-based toolbar color.
   */
  @Input() toolbarColor?: string;

  /**
   * Optional progress bar color override.
   * If provided, this will override the default progress bar color.
   * Defaults to 'medium' if not provided.
   * Use 'medium' (light gray) for blue toolbars to ensure visibility.
   */
  @Input() progressBarColor: string = 'medium';

  /**
   * Whether to render pinned info content provided via InfoPinService
   * (e.g. workspace/session cards under the main toolbar).
   */
  @Input() showPinnedContent: boolean = true;

  /**
   * Page-level load function.
   *
   * Contract:
   * - Return an Observable that **completes** when the load succeeds.
   * - Return an Observable that **errors** when the load fails.
   *
   * The `TabPageComponent` will:
   * - wait the configured stall period before subscribing (to avoid wasteful calls during rapid navigation)
   * - unsubscribe on navigation away (cancels Angular `HttpClient` requests)
   * - retry on re-entry until the first successful completion happens
   */
  @Input() loadFn?: () => Observable<unknown>;

  /**
   * Optional refresh function for manual refresh actions.
   * Defaults to `loadFn` when not provided.
   */
  @Input() refreshFn?: () => Observable<unknown>;

  /**
   * Event emitted when the refresh button is clicked.
   * Pages should bind to this event and call their data loading method.
   */
  @Output() refresh = new EventEmitter<void>();

  /**
   * Event emitted automatically when the page needs to load data.
   * This happens on initial load and when returning to a page that hasn't loaded yet.
   * Pages should bind to this event and call their data loading method.
   * Example: (load)="loadData()"
   */
  @Output() load = new EventEmitter<void>();

  /**
   * Toolbar color based on current theme (dark or light)
   */
  computedToolbarColor: string = 'primary';

  /**
   * Loading state from global loading service
   */
  isLoading = false;
  loadingPhase: LoadingPhase = null;
  isLoadInFlight = false;
  isRefreshCoolingDown = false;

  /**
   * Tracks whether the page has completed a successful load at least once.
   * Used to stop auto-loading after the first success.
   */
  private hasLoadedSuccessfullyOnce = false;
  private activeLoadSubscription?: Subscription;
  private refreshCooldownTimeoutId?: number;

  /**
   * Auto-load state: wait a configurable stall period before subscribing to `loadFn`.
   * Cancel if navigation occurs before the stall completes or while the load is in-flight.
   */
  isAutoLoadStalling = false;
  private autoLoadStallMs = DEFAULT_APP_SETTINGS.requestStallMs;
  private autoLoadTimeoutId?: number;
  private autoLoadCheckTimeoutId?: number;
  private isViewActive = false;
  private hasTriggeredLoadWhileActive = false;
  private subscriptions = new Subscription();
  private themeSubscription?: Subscription;
  private loadingSubscription?: Subscription;
  private loadingPhaseSubscription?: Subscription;
  private themeService = inject(ThemeService);
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private appSettings = inject(APP_SETTINGS_TOKEN);
  private infoPinService = inject(InfoPinService);
  private refreshCooldownMs = DEFAULT_APP_SETTINGS.refreshCooldownMs;

  pinnedContent: TemplateRef<unknown> | null = null;
  pinnedContentContext: Record<string, unknown> = {};

  ngOnInit(): void {
    // Set initial toolbar color
    this.updateToolbarColor();

    // Subscribe to theme changes
    this.themeSubscription = this.themeService.currentTheme$.subscribe(() => {
      if (!this.toolbarColor) {
        this.updateToolbarColor();
      }
    });

    // Subscribe to loading state changes
    this.loadingSubscription = this.loadingService.isLoading$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    // Cancel any pending auto-load on navigation start; re-check on navigation end.
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd))
        .subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.cancelPendingAutoLoad();
          } else {
            this.scheduleAutoLoadCheck();
          }
        }),
    );

    // Subscribe to loading phase changes
    this.loadingPhaseSubscription = this.loadingService.loadingPhase$.subscribe((phase) => {
      this.loadingPhase = phase;
    });

    this.subscriptions.add(
      this.appSettings.settings$.subscribe((settings) => {
        this.refreshCooldownMs =
          settings?.refreshCooldownMs ?? DEFAULT_APP_SETTINGS.refreshCooldownMs;
        this.autoLoadStallMs = settings?.requestStallMs ?? DEFAULT_APP_SETTINGS.requestStallMs;
      }),
    );

    // Subscribe to pinned content from info components
    this.subscriptions.add(
      this.infoPinService.getPinnedContent$().subscribe((content) => {
        this.pinnedContent = content?.template ?? null;
        this.pinnedContentContext = content?.context ?? {};
      }),
    );
  }

  ngAfterViewInit(): void {
    // Initial activation check happens after view is rendered so visibility checks work.
    this.scheduleAutoLoadCheck();
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    if (this.loadingPhaseSubscription) {
      this.loadingPhaseSubscription.unsubscribe();
    }
    this.subscriptions.unsubscribe();
    this.cancelPendingAutoLoad();
    this.cancelActiveLoad();
    if (this.autoLoadCheckTimeoutId) {
      clearTimeout(this.autoLoadCheckTimeoutId);
      this.autoLoadCheckTimeoutId = undefined;
    }
    if (this.refreshCooldownTimeoutId) {
      clearTimeout(this.refreshCooldownTimeoutId);
      this.refreshCooldownTimeoutId = undefined;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['toolbarColor']) {
      this.updateToolbarColor();
    }
    if (changes['loadFn']) {
      // If the load function changes, treat it as "not loaded" and re-evaluate auto-load.
      this.hasLoadedSuccessfullyOnce = false;
      this.cancelPendingAutoLoad();
      this.cancelActiveLoad();
      this.scheduleAutoLoadCheck();
    }
  }

  private scheduleAutoLoadCheck(): void {
    if (this.autoLoadCheckTimeoutId) {
      return;
    }
    this.autoLoadCheckTimeoutId = window.setTimeout(() => {
      this.autoLoadCheckTimeoutId = undefined;
      this.checkViewActiveAndMaybeAutoLoad();
    }, 0);
  }

  private checkViewActiveAndMaybeAutoLoad(): void {
    const activeNow = this.computeIsViewActive();
    if (!activeNow) {
      if (this.isViewActive) {
        this.onBecameInactive();
      }
      return;
    }

    if (!this.isViewActive) {
      this.onBecameActive();
    }

    this.maybeScheduleAutoLoad();
  }

  private onBecameActive(): void {
    this.isViewActive = true;
    this.hasTriggeredLoadWhileActive = false;
    if (this.reloadDataOnEachViewActivation) {
      this.hasLoadedSuccessfullyOnce = false;
    }
  }

  private onBecameInactive(): void {
    this.isViewActive = false;
    this.hasTriggeredLoadWhileActive = false;
    this.cancelPendingAutoLoad();
    this.cancelActiveLoad();
  }

  private maybeScheduleAutoLoad(): void {
    if (!this.isViewActive) {
      return;
    }
    if (this.hasLoadedSuccessfullyOnce) {
      this.cancelPendingAutoLoad();
      return;
    }
    if (this.isLoadInFlight) {
      return;
    }
    if (this.hasTriggeredLoadWhileActive || this.autoLoadTimeoutId) {
      return;
    }

    const stallMs = Math.max(0, Math.round(this.autoLoadStallMs || 0));
    if (stallMs > 0) {
      this.isAutoLoadStalling = true;
    }
    this.autoLoadTimeoutId = window.setTimeout(() => {
      this.autoLoadTimeoutId = undefined;
      this.isAutoLoadStalling = false;

      if (!this.isViewActive) {
        return;
      }
      if (
        this.hasLoadedSuccessfullyOnce ||
        this.isLoadInFlight ||
        this.hasTriggeredLoadWhileActive
      ) {
        return;
      }

      this.hasTriggeredLoadWhileActive = true;
      this.startLoad('auto');
    }, stallMs);
  }

  private cancelPendingAutoLoad(): void {
    if (this.autoLoadTimeoutId) {
      clearTimeout(this.autoLoadTimeoutId);
      this.autoLoadTimeoutId = undefined;
    }
    this.isAutoLoadStalling = false;
  }

  private computeIsViewActive(): boolean {
    const host = this.elementRef.nativeElement;
    if (!host.isConnected) {
      return false;
    }

    const pageEl = host.querySelector('.ion-page') as HTMLElement | null;
    const el = pageEl ?? host;

    // `hidden` maps to `display: none`, so treat as inactive.
    if ((el as any).hidden) {
      return false;
    }

    // If Ionic/Angular detaches or hides the page, this will be empty.
    return el.getClientRects().length > 0;
  }

  private updateToolbarColor(): void {
    if (this.toolbarColor) {
      this.computedToolbarColor = this.toolbarColor;
      return;
    }
    const currentTheme = this.themeService.getCurrentTheme();
    if (currentTheme) {
      const theme = getTheme(currentTheme);
      if (theme) {
        // Use 'dark' color for dark themes, 'light' for light themes
        // This ensures proper contrast
        this.computedToolbarColor = theme.isDark ? 'dark' : 'light';
      }
    }
  }

  /**
   * Computed progress bar color based on loading phase.
   * Light gray (medium) during stall, green (success) during active loading.
   */
  /** Merges {@link contentClass} with non-scroll mode class for `ion-content`. */
  get contentHostClasses(): string {
    const base = (this.contentClass ?? '').trim();
    return [base, !this.contentScrollY ? 'tab-page-content--no-scroll' : '']
      .filter(Boolean)
      .join(' ');
  }

  get computedProgressBarColor(): string {
    if (this.isAutoLoadStalling) {
      return 'medium';
    }
    if (this.loadingPhase === 'stall') {
      return 'medium';
    } else if (this.loadingPhase === 'active') {
      return 'success';
    }
    return this.progressBarColor;
  }

  private cancelActiveLoad(): void {
    if (this.activeLoadSubscription) {
      this.activeLoadSubscription.unsubscribe();
    }
  }

  private startRefreshCooldown(): void {
    const cooldownMs = Math.max(0, Math.round(this.refreshCooldownMs || 0));
    if (cooldownMs <= 0) {
      return;
    }
    this.isRefreshCoolingDown = true;
    if (this.refreshCooldownTimeoutId) {
      clearTimeout(this.refreshCooldownTimeoutId);
    }
    this.refreshCooldownTimeoutId = window.setTimeout(() => {
      this.isRefreshCoolingDown = false;
      this.refreshCooldownTimeoutId = undefined;
    }, cooldownMs);
  }

  private startLoad(kind: 'auto' | 'refresh', completeRefresher?: () => void): void {
    if (this.activeLoadSubscription) {
      completeRefresher?.();
      return;
    }

    const fn = kind === 'refresh' ? (this.refreshFn ?? this.loadFn) : this.loadFn;

    // Backwards-compatible events (for callers that still use `(load)` / `(refresh)`).
    if (!fn) {
      if (kind === 'refresh') {
        this.refresh.emit();
      } else {
        this.load.emit();
      }
      completeRefresher?.();
      return;
    }

    this.isLoadInFlight = true;
    let didComplete = false;
    let didError = false;

    try {
      this.activeLoadSubscription = fn()
        .pipe(
          finalize(() => {
            const didEnd = didComplete || didError;
            this.activeLoadSubscription = undefined;
            this.isLoadInFlight = false;

            completeRefresher?.();

            if (kind === 'refresh' && didEnd) {
              this.startRefreshCooldown();
            }

            if (didComplete && !didError) {
              this.hasLoadedSuccessfullyOnce = true;
            }
          }),
        )
        .subscribe({
          complete: () => {
            didComplete = true;
          },
          error: () => {
            didError = true;
          },
        });
    } catch (error) {
      console.error('[TabPageComponent] loadFn threw:', error);
      this.activeLoadSubscription = undefined;
      this.isLoadInFlight = false;
      completeRefresher?.();
      if (kind === 'refresh') {
        this.startRefreshCooldown();
      }
    }
  }

  onRefresh(force = false): void {
    if (!force && (this.isLoading || this.isLoadInFlight || this.isRefreshCoolingDown)) {
      return;
    }
    this.cancelPendingAutoLoad();
    this.startLoad('refresh');
  }

  onIonRefresh(event: any): void {
    if (this.isLoading || this.isLoadInFlight || this.isRefreshCoolingDown) {
      event.target.complete();
      return;
    }
    this.cancelPendingAutoLoad();
    this.startLoad('refresh', () => event.target.complete());
  }
}
